import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

interface OrgEvent {
  id: string;
  name: string;
  description: string;
  startDate: string;
}

export const UpcomingOrgEvents: React.FC = ({ orgId }: { orgId: string }) => {
  const { data: events = [], isLoading, isError } = useQuery<OrgEvent[]>({
    queryKey: ['orgUpcomingEvents'],
    queryFn: async () => {
      const res = await fetch(`/api/org/${orgId}/events/upcoming`);
      if (!res.ok) throw new Error('Failed to fetch organization events');
      return res.json();
    },
  });

  if (isLoading) return (
    <div className="flex justify-center p-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );

  if (isError) return (
    <div className="bg-red-50 p-4 rounded-md text-red-800">
      Failed to load organization events.
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

      {events.length === 0 && (
        <div className="col-span-4 p-4 text-center text-muted-foreground">
          No upcoming events.
        </div>
      )}
      {events.map((event) => (
        <Card key={event.id} className="overflow-hidden">
          <CardHeader>
            <CardTitle>{event.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-ellipsis h-12">
              {event.description}
            </p>
            <p className="text-xs mt-2">{new Date(event.startDate).toLocaleString()}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
